import { useEffect, useState } from "react";
import { Navigate, useParams } from "react-router";
import { useForm } from "react-hook-form";
import { useFetchSubmittedForm } from "../../hooks/api/form/useFetchSubmittedForm";
import { Box } from "~/components/Base/Box/Box";
import { FormHeader } from "../Form/FormHeader/FormHeader";
import { FormContent } from "../Form/FormContent/FormContent";
import { i18n } from "~/localization/i18n";
import { convertFormValidation } from "../Form/shared/convertFormValidation";
import { applyStoredVisibility } from "../Form/shared/form-visibility.js";
import "./Pdf.css";
import { useAuth } from "react-oidc-context";
import { getCookie } from "~/helpers/cookies";
import { DateDisplayFormatContext } from "~/components/Base/DateTimePicker/DateDisplayFormatContext";
import { getDateFnsLocale } from "~/helpers/date-fns-locale";

export function Pdf() {
  const { isAuthenticated, isLoading: isLoadingAuth } = useAuth();
  const { control } = useForm({ delayError: 500, mode: "onChange" });
  const { formId } = useParams();
  const {
    submittedForm,
    isLoading: isLoadingSubmittedForm,
    error,
  } = useFetchSubmittedForm(formId, (data) => {
    const form = data.questModel;
    convertFormValidation(form);
    // A submitted form renders from its stored visibility values; expressions
    // are only evaluated as a fallback for payloads without them.
    applyStoredVisibility(form);
  });
  const hasTokenInCookie = getCookie(import.meta.env.VITE_TOKEN_COOKIE_NAME) != null;

  const studyFormats = submittedForm?.studyDateTimeFormat ?? null;
  const formLanguage = submittedForm?.questModel.identifierModel.Language;

  // Load the date-fns locale once, before rendering the form, so every
  // DateTimePicker formats synchronously and the PDF renderer can never
  // snapshot a page formatted with the default (English) locale
  const [dateDisplayFormat, setDateDisplayFormat] = useState(null);

  useEffect(() => {
    if (!studyFormats) {
      return;
    }

    // The study's date language, or the subject's form language when
    // Admin has "user selected language" (empty dateLanguage)
    const language = studyFormats.dateLanguage || formLanguage || i18n.language;

    let cancelled = false;

    getDateFnsLocale(language).then((locale) => {
      if (!cancelled) {
        setDateDisplayFormat({ ...studyFormats, locale });
      }
    });

    return () => {
      cancelled = true;
    };
  }, [studyFormats, formLanguage]);

  // Wait for the authentication to be loaded before redirecting
  // Skip waiting for the authentication to be loaded if the token is in the cookie
  if (!hasTokenInCookie && isLoadingAuth) {
    return null;
  }

  if (!hasTokenInCookie && !isAuthenticated) {
    return <Navigate to="/" />;
  }

  if (isLoadingSubmittedForm) {
    return (
      <div>
        <b>loading...</b>
      </div>
    );
  }

  if (error) {
    return (
      <div>
        {error.status} {error.statusText} - {error.url}
      </div>
    );
  }

  if (studyFormats && dateDisplayFormat === null) {
    return (
      <div>
        <b>loading...</b>
      </div>
    );
  }

  const questModel = submittedForm.questModel;

  if (
    questModel.identifierModel.Language !== null &&
    i18n.language !== questModel.identifierModel.Language
  ) {
    i18n.changeLanguage(questModel.identifierModel.Language);
  }
  const metadata = submittedForm.pdfMetadataViewModel;

  // The pdf-header and pdf-footer are picked automatically out of the HTML when generating the PDF
  const pdfHeader = [
    metadata.subjectKey,
    metadata.formDisplayLabel,
    metadata.activityName,
    metadata.studyEventLabel,
    metadata.siteName,
    metadata.studyName,
  ].join(" | ");

  const pdfFooter = [
    metadata.formAuthor,
    metadata.protocolVersion,
    metadata.version,
    metadata.dateTime,
    "{pageNumber} of {totalPages}",
  ].join(" | ");

  return (
    // The PDF is displayed in Viedoc Clinic, so dates must use the study's
    // date/time settings (configured in Admin) instead of the locale of the
    // machine that renders the PDF. Only this route provides the context;
    // the normal Viedoc Me form keeps using the subject's device locale.
    <DateDisplayFormatContext.Provider value={dateDisplayFormat}>
      <div className="pdf">
        <div id="pdf-header" className="pdf__header">
          {pdfHeader}
        </div>
        <div id="pdf-footer" className="pdf__footer">
          {pdfFooter}
        </div>
        {questModel.itemGroups
          .filter((currentItemGroup) => currentItemGroup.isVisible)
          .map((currentItemGroup, index) => {
            return (
              <div className="pdf__page" key={currentItemGroup?.oid ?? index}>
                <Box>
                  <FormHeader
                    currentPage={index + 1}
                    formName={questModel.formName}
                    itemGroupLabel={currentItemGroup?.label}
                    pageCount={questModel.itemGroups.length}
                    valid={true}
                    isPending={false}
                    isReadyToSubmit={true}
                    sticky={false}
                  />
                  <FormContent
                    control={control}
                    errors={null}
                    formData={questModel.formData}
                    itemGroups={[currentItemGroup]}
                    readOnly={true}
                    onChange={() => {}}
                  />
                </Box>
              </div>
            );
          })}
      </div>
    </DateDisplayFormatContext.Provider>
  );
}
